import { Column, ColumnDef, Row } from "@tanstack/react-table";
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { Button } from "../../button";
import { ArrowUpDown, Pencil, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import { cn } from "../../../utils/cn";
import {
  Device,
  STATUS_IDS,
  USER_ROLES_IDS,
  UserAppointment,
} from "../../../constants/common";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import useAuthStore from "../../../store/Auth";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../../inputs/TextInput";

interface DeviceDetailsTableProps {
  addedDevices: any;
  setAddedDevices: Dispatch<any>;
  userAppointmentDetails?: UserAppointment;
  onDeviceEdit: (id: string) => void;
  isSubmitDisabled?: boolean;
  isUserAppointmentApprover: boolean;
  approvalStatus?: STATUS_IDS;
  setIsDeviceDeleted: Dispatch<SetStateAction<boolean>>;
}

const DeviceDetailsTable = ({
  addedDevices,
  setAddedDevices,
  onDeviceEdit,
  isSubmitDisabled,
  isUserAppointmentApprover,
  approvalStatus,
  setIsDeviceDeleted,
}: DeviceDetailsTableProps) => {
  const { t } = useTranslation();

  const user = useAuthStore((state) => state.user);

  const { control } = useForm();

  const params = useLocation();

  const isAllDeviceStatusesPending = addedDevices?.every(
    (item: Device) => item.currentUserDeviceStatus === STATUS_IDS.PENDING
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAddedDevices((prev: any) =>
        prev.filter(
          (visitor: { id: string; appointmentDeviceId: number }) =>
            (visitor.id || visitor.appointmentDeviceId) != id
        )
      );
      setIsDeviceDeleted(true);
    },
    [setAddedDevices, setIsDeviceDeleted]
  );

  const handleCommentChange = useCallback(
    (deviceId: number, comment: string) => {
      setAddedDevices?.((prev?: Device[]) =>
        prev?.map((d: Device) =>
          d.appointmentDeviceId === deviceId ? { ...d, comment } : d
        )
      );
    },
    [setAddedDevices]
  );

  const isApproveRequest = params.pathname === APP_ROUTES.APPROVE_REQUEST;
  const isStaff = user?.n_RoleID === USER_ROLES_IDS.STAFF;
  const isSecurityUser = user?.n_RoleID === USER_ROLES_IDS.SECURITY;
  const isSecurityManager = user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER;

  const columns: ColumnDef<Device>[] = useMemo(
    () =>
      [
        {
          accessorKey: "deviceType",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("deviceType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            return (
              <span>
                {row.original.deviceType !== 2250
                  ? row.original.deviceType
                  : `${
                      row.original.deviceTypeOther ||
                      row.original.deviceTypeOthersValue
                    } (${t("other")})`}
              </span>
            );
          },
        },
        {
          accessorKey: "deviceModel",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("deviceModel")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            return <span>{row.original.deviceModel}</span>;
          },
        },
        {
          accessorKey: "serialNumber",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("serialNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            return <span>{row.original.serialNumber}</span>;
          },
        },
        {
          accessorKey: "purpose",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("devicePurpose")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          // cell: ({ row }: { row: Row<Device> }) => {
          //   return (
          //     <span>
          //       {row.original.devicePurpose !== 2254
          //         ? row.original.devicePurpose
          //         : `${
          //             row.original.devicePurposeOther ||
          //             row.original.devicePurposeOthersValue
          //           } (${t("other")})`}
          //     </span>
          //   );
          // },
        },
        {
          accessorKey: "currentUserDeviceStatus",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("currentApprovalStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            if (!row.original.appointmentDeviceId) return <></>;

            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 max-w-[100px]",
                  !row.original.currentUserDeviceStatus
                    ? row.original.approvalStatus === STATUS_IDS.APPROVED
                      ? "bg-green-600"
                      : row.original.approvalStatus === STATUS_IDS.PENDING
                      ? "bg-yellow-500"
                      : "bg-red-500"
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.APPROVED
                    ? "bg-green-600"
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.PENDING
                    ? "bg-yellow-500"
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.REQUEST_INFO
                    ? "bg-purple-tertiary"
                    : "bg-red-500"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {!row.original.currentUserDeviceStatus
                    ? row.original.approvalStatus === STATUS_IDS.APPROVED
                      ? t("approved")
                      : row.original.approvalStatus === STATUS_IDS.PENDING
                      ? t("pending")
                      : row.original.approvalStatus === STATUS_IDS.REJECTED
                      ? t("rejected")
                      : row.original.approvalStatus === STATUS_IDS.CANCELED
                      ? t("canceled")
                      : row.original.approvalStatus === STATUS_IDS.REQUEST_INFO
                      ? t("requestInfo")
                      : t("expired")
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.APPROVED
                    ? t("approved")
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.PENDING
                    ? t("pending")
                    : row.original.currentUserDeviceStatus ===
                      STATUS_IDS.REQUEST_INFO
                    ? t("requestInfo")
                    : row.original.approvalStatus === STATUS_IDS.REJECTED
                    ? t("rejected")
                    : row.original.approvalStatus === STATUS_IDS.CANCELED
                    ? t("canceled")
                    : t("expired")}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "approvalStatus",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("approvalStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            if (!row.original.appointmentDeviceId) return <></>;

            return row.original.approvalStatus ? (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 max-w-[100px]",
                  row.original.approvalStatus === STATUS_IDS.APPROVED
                    ? "bg-green-600"
                    : row.original.approvalStatus === STATUS_IDS.PENDING
                    ? "bg-yellow-500"
                    : row.original.approvalStatus === STATUS_IDS.REQUEST_INFO
                    ? "bg-purple-tertiary"
                    : "bg-red-500"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {row.original.approvalStatus === STATUS_IDS.APPROVED
                    ? t("approved")
                    : row.original.approvalStatus === STATUS_IDS.PENDING
                    ? t("pending")
                    : row.original.approvalStatus === STATUS_IDS.REQUEST_INFO
                    ? t("requestInfo")
                    : row.original.approvalStatus === STATUS_IDS.REJECTED
                    ? t("rejected")
                    : row.original.approvalStatus === STATUS_IDS.CANCELED
                    ? t("canceled")
                    : t("expired")}
                </p>
              </div>
            ) : (
              <></>
            );
          },
        },
        {
          accessorKey: "action",
          header: t("action"),
          cell: ({ row }: { row: Row<Device> }) => {
            return (
              <div className="flex items-center gap-1">
                {!(
                  isApproveRequest &&
                  (!isStaff || !isUserAppointmentApprover || isSubmitDisabled)
                ) && (
                  <Button
                    type="button"
                    onClick={() =>
                      onDeviceEdit(
                        row.original.appointmentDeviceId
                          ? row.original.appointmentDeviceId.toString()
                          : row.original.id
                      )
                    }
                    disabled={
                      row.original.currentUserDeviceStatus ===
                      STATUS_IDS.APPROVED
                    }
                    className="py-0.5"
                  >
                    <Pencil className="w-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() =>
                    handleDelete(
                      row.original.appointmentDeviceId
                        ? row.original.appointmentDeviceId.toString()
                        : row.original.id
                    )
                  }
                  disabled={
                    !isSecurityUser &&
                    !isSecurityManager &&
                    row.original.currentUserDeviceStatus === STATUS_IDS.APPROVED
                  }
                  className="bg-red-600 hover:bg-red-500 py-0.5"
                >
                  <Trash className="w-4" />
                </Button>
              </div>
            );
          },
        },
        {
          accessorKey: "comment",
          header: ({ column }: { column: Column<Device> }) => (
            <Button
              className="flex w-full justify-between text-md min-w-72"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("comment")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<Device> }) => {
            if (
              row.original.currentUserDeviceStatus ===
                STATUS_IDS.REQUEST_INFO ||
              (params.pathname === APP_ROUTES.UPDATE_REQUEST &&
                row.original.approvalStatus !== STATUS_IDS.APPROVED)
            ) {
              const deviceId = row.original.appointmentDeviceId;

              return (
                <Controller
                  name={`comments.${deviceId}`}
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                      disabled={
                        row.original.currentUserDeviceStatus ===
                        STATUS_IDS.APPROVED
                      }
                      onBlur={(e) => {
                        handleCommentChange?.(deviceId, e.target.value);
                      }}
                      className="py-2 rounded-[12px]"
                    />
                  )}
                />
              );
            }
          },
        },
      ].filter((column) => {
        let excludeKeys = [];

        if (
          params.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
          params.pathname === APP_ROUTES.GROUP_VISIT_REQUEST ||
          params.pathname === APP_ROUTES.VIP_VISIT_REQUEST
        ) {
          excludeKeys.push(
            "approvalStatus",
            "currentUserDeviceStatus",
            "comment"
          );
        }

        const isUpdateRequest = params.pathname === APP_ROUTES.UPDATE_REQUEST;

        if (isUpdateRequest) {
          excludeKeys.push("currentUserDeviceStatus");

          if (approvalStatus !== STATUS_IDS.REQUEST_INFO) {
            excludeKeys.push("comment");
          }

          if (isSubmitDisabled) {
            excludeKeys.push("action");
          }
        }

        if (isApproveRequest) {
          if (!isStaff && !isSecurityUser && !isSecurityManager) {
            excludeKeys.push("action");
          }

          if (
            !isUserAppointmentApprover ||
            (isSubmitDisabled && !isSecurityUser && !isSecurityManager)
          ) {
            excludeKeys.push("action", "comment");
          }

          if (
            approvalStatus !== STATUS_IDS.REQUEST_INFO ||
            isAllDeviceStatusesPending
          ) {
            excludeKeys.push("comment");
          }
        }

        return !excludeKeys.includes(column.accessorKey);
      }),
    [
      t,
      isApproveRequest,
      isStaff,
      isUserAppointmentApprover,
      isSubmitDisabled,
      isSecurityUser,
      isSecurityManager,
      onDeviceEdit,
      handleDelete,
      params.pathname,
      control,
      handleCommentChange,
      approvalStatus,
      isAllDeviceStatusesPending,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col">
        <TableWrapper
          data={addedDevices}
          columns={columns}
          isPagination={false}
          isColumnChooser={false}
        />
      </div>

      {/* <ApproveRejectDeviceModal
        appointmentId={userAppointmentDetails?.id}
        selectedDevicesStatusId={selectedDevicesStatusId}
        setAddedDevices={setAddedDevices}
        isOpen={isApproveRejectDeviceModalOpen}
        onOpenChange={() => setIsApproveRejectDeviceModalOpen(false)}
        setIsHideBtns={setIsHideBtns}
        addedDevices={addedDevices}
        setIsDevicesApprovalPending={setIsDevicesApprovalPending}
        appointmentDateTime={{
          visitStartDate: userAppointmentDetails?.visitStartDate,
          visitEndDate: userAppointmentDetails?.visitEndDate,
        }}
      /> */}
    </>
  );
};

export default React.memo(DeviceDetailsTable);
